class LpStatsPanel extends HTMLElement {
  #abort = new AbortController();
  #observer = null;
  #hasAnimated = false;

  connectedCallback() {
    const animation = this.dataset.counterAnimation || 'none';
    if (animation === 'none') return;

    const counters = this.querySelectorAll('[data-counter-value]');
    if (!counters.length) return;

    this.#prepareCounters(counters, animation);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishCounters(counters, animation);
      return;
    }

    const threshold = Number.parseFloat(this.dataset.counterThreshold) || 0.1;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || this.#hasAnimated) return;

        this.#hasAnimated = true;
        this.#runAnimation(counters, animation);
        this.#observer?.disconnect();
      },
      { threshold }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  #prepareCounters(counters, animation) {
    counters.forEach((counter, index) => {
      counter.classList.add(`s-lp-stats-panel__stat-value--${animation}`);
      counter.style.setProperty('--lp-sp-stagger', `${index * 120}ms`);

      const display = counter.querySelector('.s-lp-stats-panel__stat-value-display');
      if (!display) return;

      const parsed = this.#parseValue(counter.dataset.counterValue);
      if (animation === 'count_up' && parsed.number !== null) {
        display.textContent = `0${parsed.suffix}`;
      } else {
        display.textContent = parsed.display;
      }
    });
  }

  #runAnimation(counters, animation) {
    counters.forEach((counter, index) => {
      const delay = index * 120;

      if (animation === 'count_up') {
        const parsed = this.#parseValue(counter.dataset.counterValue);
        if (parsed.number !== null) {
          window.setTimeout(() => {
            counter.classList.add('is-animated');
            this.#countUp(counter, parsed);
          }, delay);
          return;
        }
      }

      window.setTimeout(() => {
        counter.classList.add('is-animated');
      }, delay);
    });
  }

  #finishCounters(counters, animation) {
    counters.forEach((counter) => {
      const parsed = this.#parseValue(counter.dataset.counterValue);
      const display = counter.querySelector('.s-lp-stats-panel__stat-value-display');

      if (display) {
        display.textContent = parsed.display;
      }

      counter.classList.add(`s-lp-stats-panel__stat-value--${animation}`, 'is-animated');
    });
  }

  #parseValue(raw) {
    const value = (raw || '').trim();
    const match = value.match(/^([\d.,]+)(.*)$/);

    if (!match) {
      return { number: null, suffix: '', display: value, isInteger: true };
    }

    const numeric = Number.parseFloat(match[1].replace(/,/g, ''));

    if (Number.isNaN(numeric)) {
      return { number: null, suffix: '', display: value, isInteger: true };
    }

    return {
      number: numeric,
      suffix: match[2],
      display: value,
      isInteger: !match[1].includes('.'),
    };
  }

  #countUp(counter, parsed) {
    const display = counter.querySelector('.s-lp-stats-panel__stat-value-display');
    if (!display) return;

    const duration = 1400;
    const start = performance.now();
    const target = parsed.number;
    const suffix = parsed.suffix;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      const current = target * eased;
      const formatted = parsed.isInteger ? Math.round(current) : current.toFixed(1);

      display.textContent = `${formatted}${suffix}`;

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('lp-stats-panel')) {
  customElements.define('lp-stats-panel', LpStatsPanel);
}
